// DocsSheet.tsx — STEP-DOCS-PWA-READ-SURFACE
//
// A bottom sheet for viewing a document from the Postgres docs store.
// Opened via openDocSheet(path) from the Entities screen docs rows.
// Fetches content via fetchDocContent(path) and renders with renderMarkdown().
// Agent/skill docs show a structured metadata panel above the markdown body
// (AC2/AC3 from STEP-99907-R2).

import { useEffect, useState } from "react";
import { fetchDocContent } from "../api/client.js";
import { renderMarkdown } from "../lib/renderMarkdown.js";
import {
  parseAgentMetadata,
  parseSkillMetadata,
  type AgentMetadata,
  type SkillMetadata,
} from "../lib/parseFrontmatter.js";

// Module-level state — simple single-doc sheet (no stack needed).
let _openDoc: ((path: string, title?: string) => void) | null = null;
let _closeDoc: (() => void) | null = null;

export function openDocSheet(path: string, title?: string): void {
  _openDoc?.(path, title);
}

export function closeDocSheet(): void {
  _closeDoc?.();
}

// ---------------------------------------------------------------------------
// Metadata panel components
// ---------------------------------------------------------------------------

const FIELD_LABEL_STYLE = {
  fontFamily: "var(--font-mono)",
  fontSize: 9,
  textTransform: "uppercase" as const,
  letterSpacing: "0.08em",
  color: "var(--text-dim)",
  marginBottom: 3,
};

const CHIP_ROW_STYLE = {
  display: "flex",
  flexWrap: "wrap" as const,
  gap: 4,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function FieldBlock({ label, children }: { label: string; children: any }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={FIELD_LABEL_STYLE}>{label}</div>
      {children}
    </div>
  );
}

export function AgentMetaPanel({ meta }: { meta: AgentMetadata }) {
  return (
    <div
      style={{
        margin: "0 0 16px",
        padding: "14px 16px",
        background: "var(--bg-elev)",
        border: "1px solid var(--border-soft)",
        borderRadius: 8,
        fontSize: 12,
      }}
    >
      {meta.name && <FieldBlock label="Name">{meta.name}</FieldBlock>}

      {meta.description && (
        <FieldBlock label="Description">
          <div
            style={{ color: "var(--text)", lineHeight: 1.4, fontSize: 12 }}
          >
            {meta.description}
          </div>
        </FieldBlock>
      )}

      {meta.model && (
        <FieldBlock label="Model">
          <span className="chip" style={{ fontFamily: "var(--font-mono)", fontSize: 10 }}>
            {meta.model}
          </span>
          {meta.effort && (
            <span
              className="chip"
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                marginLeft: 4,
              }}
            >
              {meta.effort}
            </span>
          )}
        </FieldBlock>
      )}

      {meta.tools.length > 0 && (
        <FieldBlock label="Tools">
          <div style={CHIP_ROW_STYLE}>
            {meta.tools.map((t) => (
              <span
                key={t}
                className="chip"
                style={{ fontFamily: "var(--font-mono)", fontSize: 10 }}
              >
                {t}
              </span>
            ))}
          </div>
        </FieldBlock>
      )}

      {meta.skills.length > 0 && (
        <FieldBlock label="Skills">
          <div style={CHIP_ROW_STYLE}>
            {meta.skills.map((slug) => (
              <button
                key={slug}
                onClick={() =>
                  openDocSheet(`.claude/skills/${slug}/SKILL.md`, slug)
                }
                style={{
                  background: "var(--accent-soft, rgba(99,102,241,0.12))",
                  border: "1px solid var(--accent, #6366f1)",
                  borderRadius: 4,
                  padding: "2px 8px",
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--accent, #6366f1)",
                  cursor: "pointer",
                }}
              >
                {slug}
              </button>
            ))}
          </div>
        </FieldBlock>
      )}

      {meta.contractIds.length > 0 && (
        <FieldBlock label="Contracts">
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {meta.contractIds.map((id) => (
              <span
                key={id}
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--text-dim)",
                }}
              >
                {id}
              </span>
            ))}
          </div>
        </FieldBlock>
      )}
    </div>
  );
}

export function SkillMetaPanel({ meta }: { meta: SkillMetadata }) {
  return (
    <div
      style={{
        margin: "0 0 16px",
        padding: "14px 16px",
        background: "var(--bg-elev)",
        border: "1px solid var(--border-soft)",
        borderRadius: 8,
        fontSize: 12,
      }}
    >
      {meta.name && <FieldBlock label="Name">{meta.name}</FieldBlock>}

      {meta.description && (
        <FieldBlock label="Description">
          <div style={{ color: "var(--text)", lineHeight: 1.4, fontSize: 12 }}>
            {meta.description}
          </div>
        </FieldBlock>
      )}

      {meta.procedureSummary && (
        <FieldBlock label="Procedure">
          <div style={{ color: "var(--text)", lineHeight: 1.4, fontSize: 12 }}>
            {meta.procedureSummary}
          </div>
        </FieldBlock>
      )}

      {meta.grain && (
        <FieldBlock label="Grain">
          <span className="chip" style={{ fontFamily: "var(--font-mono)", fontSize: 10 }}>
            {meta.grain}
          </span>
        </FieldBlock>
      )}

      {meta.steward && (
        <FieldBlock label="Steward">
          <span className="chip" style={{ fontFamily: "var(--font-mono)", fontSize: 10 }}>
            {meta.steward}
          </span>
        </FieldBlock>
      )}

      {meta.triggerConditions && meta.triggerConditions.length > 0 && (
        <FieldBlock label="Triggers">
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {meta.triggerConditions.map((t) => (
              <span
                key={t}
                style={{ color: "var(--text)", lineHeight: 1.4, fontSize: 12 }}
              >
                {t}
              </span>
            ))}
          </div>
        </FieldBlock>
      )}
    </div>
  );
}

interface DocState {
  path: string;
  title: string;
}

export function DocsSheet() {
  const [doc, setDoc] = useState<DocState | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Register global open/close handlers on mount.
  useEffect(() => {
    _openDoc = (path: string, title?: string) => {
      setDoc({ path, title: title ?? path });
      setContent(null);
      setError(null);
    };
    _closeDoc = () => {
      setDoc(null);
      setContent(null);
      setError(null);
    };
    return () => {
      _openDoc = null;
      _closeDoc = null;
    };
  }, []);

  // Fetch content whenever the doc changes.
  useEffect(() => {
    if (!doc) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setContent(null);
    fetchDocContent(doc.path)
      .then((res) => {
        if (!cancelled) setContent(res.content);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Could not load document");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [doc]);

  if (!doc) return null;

  return (
    <>
      <div
        className="sheet-backdrop open"
        style={{ position: "fixed" }}
        onClick={closeDocSheet}
      />
      <div className="sheet open" style={{ position: "fixed" }}>
        <div className="sheet-handle" />
        <div className="sheet-head">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <div className="eyebrow">Doc</div>
            <button
              onClick={closeDocSheet}
              aria-label="Close"
              style={{
                background: "none",
                border: "none",
                color: "var(--text-dim)",
                fontSize: 16,
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
          <h3 style={{ fontSize: 15, lineHeight: 1.35, marginTop: 6 }}>{doc.title}</h3>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: "var(--text-dim)",
              marginTop: 4,
              wordBreak: "break-all",
            }}
          >
            {doc.path}
          </div>
        </div>

        <div className="sheet-body">
          {loading && (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                color: "var(--text-dim)",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
              }}
            >
              loading…
            </div>
          )}

          {error && !loading && (
            <div
              style={{
                padding: "20px",
                background: "var(--danger-soft)",
                border: "1px solid var(--danger)",
                borderRadius: 8,
                color: "var(--danger)",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
              }}
            >
              Could not load document: {error}
            </div>
          )}

          {!loading && !error && content && (
            <>
              {doc.path.startsWith(".claude/agents/") && (() => {
                const meta = parseAgentMetadata(content);
                return meta ? <AgentMetaPanel meta={meta} /> : null;
              })()}
              {doc.path.startsWith(".claude/skills/") && (() => {
                const meta = parseSkillMetadata(content);
                return meta ? <SkillMetaPanel meta={meta} /> : null;
              })()}
              <div
                className="doc-content"
                style={{ fontSize: 13, lineHeight: 1.6 }}
                dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}
